import React, { useEffect } from "react";
import L from "leaflet";
import { MapContainer, Marker, Popup, TileLayer, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { ProcessGeoPin, firstGeoFromMedia, formatLatLng } from "./processGeo";

type Props = {
  pins: ProcessGeoPin[];
};

const INDIA_CENTER: [number, number] = [22.5, 82];
const CLOSE_SPAN_M = 30;
const MIN_PAD_M = 20;

const letterIcon = (letter: string) =>
  L.divIcon({
    className: "hkp-process-geo-pin",
    html: `<span class="hkp-process-geo-pin__mark">${letter}</span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });

const padBounds = (bounds: L.LatLngBounds) => {
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  if (sw.distanceTo(ne) >= CLOSE_SPAN_M) {
    return bounds;
  }
  const center = bounds.getCenter();
  const latPad = MIN_PAD_M / 111320;
  const lngPad = MIN_PAD_M / (111320 * Math.max(0.2, Math.cos((center.lat * Math.PI) / 180)));
  bounds.extend([center.lat + latPad, center.lng + lngPad]);
  bounds.extend([center.lat - latPad, center.lng - lngPad]);
  return bounds;
};

const FitPins = ({ pins }: Props) => {
  const map = useMap();
  const pinKey = pins.map((p) => `${p.letter}:${p.lat}:${p.lng}`).join("|");
  useEffect(() => {
    map.invalidateSize();
    if (!pins.length) {
      map.setView(INDIA_CENTER, 5);
      return;
    }
    if (pins.length === 1) {
      map.setView([pins[0].lat, pins[0].lng], 19);
      return;
    }
    const bounds = padBounds(L.latLngBounds(pins.map((p) => [p.lat, p.lng] as [number, number])));
    map.fitBounds(bounds, { padding: [36, 36], maxZoom: 19 });
  }, [map, pinKey, pins]);
  return null;
};

const ProcessGeoMap = ({ pins }: Props) => {
  if (!pins.length) {
    return null;
  }
  return (
    <div className="hkp-process-geo">
      <p className="hkp-process-geo__title">Process locations</p>
      <MapContainer
        center={[pins[0].lat, pins[0].lng]}
        zoom={19}
        scrollWheelZoom
        className="hkp-process-geo__canvas"
      >
        <TileLayer
          attribution="&copy; OpenStreetMap"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
        />
        <FitPins pins={pins} />
        {pins.map((pin) => (
          <Marker
            key={`${pin.letter}-${pin.title}-${pin.lat}-${pin.lng}`}
            position={[pin.lat, pin.lng]}
            icon={letterIcon(pin.letter)}
          >
            <Tooltip direction="top" offset={[0, -14]}>
              {pin.title}
            </Tooltip>
            <Popup>
              <strong>
                {pin.letter}. {pin.title}
              </strong>
              <div>{formatLatLng(pin)}</div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      <ul className="hkp-process-geo__legend">
        {pins.map((pin) => (
          <li key={`${pin.letter}-legend`}>
            <span className="hkp-process-geo-pin__mark is-legend">{pin.letter}</span>
            {pin.title} · {formatLatLng(pin)}
          </li>
        ))}
      </ul>
      <p className="hkp-process-geo__hint">
        Pins mark where each process was captured. The map zooms in so even a few metres of
        separation is visible.
      </p>
    </div>
  );
};

export const ProcessGeoLine = ({ media }: { media?: any[] }) => {
  const geo = firstGeoFromMedia(media);
  if (!geo) {
    return null;
  }
  return <p className="hkp-process-geo-line">Location: {formatLatLng(geo)}</p>;
};

export default ProcessGeoMap;
